import i18next from 'i18next';
import Konva from 'konva';
import { Layer } from 'konva/lib/Layer';
import { Stage } from 'konva/lib/Stage';
import { degreesToRadians, getXProjection, getYProjection } from '../../calculation/util';
import { EDrainageTrenchType, EPosition } from '../../shared/enum';
import { redux } from '../../store';
import { EReduxState } from '../../store/enum';
import { ISrcImage } from './Index';
import { setAngleIndicator } from './Utils';

const staticWidth = 250;
const campaignPlanLayerHeight = 10;
const impermeableLayerHeight = 10;

const color = {
    initialWaterTableDepth: '#309CAE',
    waterInsideDrain: '#60C5E3',
    impermeableLayer: '#D5D3D2',
    water: '#373FCB',
};

const getFillingPercentage = (fsDrainingCapacity: number, waterTableLoweringThickness: number) => {
    let t = 1 / fsDrainingCapacity;
    if (t < waterTableLoweringThickness) return waterTableLoweringThickness * 100;
    else return t * 100;
};

export default (stage: Stage, htmlImgObj: ISrcImage, convertTo: number, onlySlopeAndLoad?: boolean, withExport?: boolean) => {
    const project = redux.getState().project.data!;
    const result = redux.getState().result;

    let initialWaterTableDepth = project.slope!.initialWaterTableDepth! * convertTo;
    let impermeableLayerDepth = project.slope!.impermeableLayerDepth! * convertTo;
    let slopeAngle = project.slope?.slopeAngle! > 45 ? 45 : project.slope?.slopeAngle!;
    let { soils } = project.soilInputData!;
    let drainPositionDepth = project.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].drainPositionDepth! * convertTo;
    let drainHeight = project.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].product?.productPhysicalDatas![0].drainHeight! * convertTo;
    let drainUnitWidth = project.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].product?.productPhysicalDatas![0].drainUnitWidth! * convertTo;
    let drainPosition = project.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].drainPosition;
    let fillingPercentage =
        result.status == EReduxState.fulfilled
            ? getFillingPercentage(
                  result.data?.phaseTwoOutputCalculation.drainingCapacity.primaryDrainageTrench.fsDrainingCapacity!,
                  project.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].waterTableLoweringThickness!,
              )
            : 0;
    let distributedOverload = project.load?.distributedOverload;

    let drainUnitLength = staticWidth / 3;

    let drainageChimneyHeight = project.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].drainageChimney?.drainageChimneyHeight! * convertTo;
    let drainageChimneyLength = project.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].drainageChimney?.drainageChimneyLength! * convertTo;

    const staticWidthX = getXProjection(staticWidth, slopeAngle);
    const campaignPlanLayerHeightX = getXProjection(campaignPlanLayerHeight, 90 - slopeAngle);

    const staticWidthY = getYProjection(staticWidth, slopeAngle);
    const campaignPlanLayerHeightY = getYProjection(campaignPlanLayerHeight, 90 - slopeAngle);
    const b = impermeableLayerDepth / Math.sin(degreesToRadians(90 - slopeAngle));

    const width = staticWidthX + campaignPlanLayerHeightX;
    const height = staticWidthY + campaignPlanLayerHeightY + b + impermeableLayerHeight;

    var layer = new Layer();

    var group = new Konva.Group({
        x: (stage.width() - width) / 2,
        y: (stage.height() - height) / 2,
        width,
        height,
        draggable: true,
    });

    if (drainPosition == EPosition.Horizontal) {
        drainHeight = drainUnitWidth;
    }

    var rect = new Konva.Rect({
        x: 0,
        y: 0,
        width,
        height,
        fill: 'transparent',
    });

    var longitudinalSection = new Konva.Shape({
        fill: 'white',
        sceneFunc: function (context, shape) {
            context.beginPath();
            context.moveTo(campaignPlanLayerHeightX, staticWidthY + campaignPlanLayerHeightY);
            context.lineTo(campaignPlanLayerHeightX, height);
            context.lineTo(width, height);
            context.lineTo(width, campaignPlanLayerHeightY);
            context.closePath();
            context.fillStrokeShape(shape);
        },
    });

    var campaignPlanLayer = new Konva.Rect({
        x: 0,
        y: staticWidthY,
        width: staticWidth,
        height: campaignPlanLayerHeight,
        rotation: -slopeAngle,
        fill: 'transparent',
    });

    var arrowDistributedOverload = [];
    let numberOfArrow = 22;
    let r = staticWidth / numberOfArrow;
    for (let index = 0; index < numberOfArrow; index++) {
        arrowDistributedOverload.push(
            new Konva.Arrow({
                x: getXProjection(r * (index + 1) - r / 2, slopeAngle),
                y: staticWidthY - getYProjection(r * (index + 1) - r / 2, slopeAngle),
                points: [0, 0, 0, campaignPlanLayerHeight - 2],
                pointerLength: 1,
                pointerWidth: 0.8,
                fill: 'black',
                stroke: 'black',
                strokeWidth: 0.8,
            }),
        );
    }

    let layerHeightFromImpermeableLayer = 0;
    var soilLayerObj = soils!
        .slice()
        .reverse()
        .map(x => {
            let color = x.color;
            let thickness = x.soilPhysicalData![0].thickness! * convertTo;
            layerHeightFromImpermeableLayer = layerHeightFromImpermeableLayer + thickness;
            return {
                color,
                thickness,
                layerHeightFromImpermeableLayer,
            };
        });

    var soilLayers = soilLayerObj.map(
        x =>
            new Konva.Shape({
                fill: x.color,
                opacity: result.status == EReduxState.fulfilled && !onlySlopeAndLoad ? 0.2 : 1,
                sceneFunc: function (context, shape) {
                    context.beginPath();
                    context.moveTo(campaignPlanLayerHeightX, height - impermeableLayerHeight - x.layerHeightFromImpermeableLayer / Math.sin(degreesToRadians(90 - slopeAngle)));
                    context.lineTo(width, height - impermeableLayerHeight - staticWidthY - x.layerHeightFromImpermeableLayer / Math.sin(degreesToRadians(90 - slopeAngle)));
                    context.lineTo(
                        width,
                        height -
                            impermeableLayerHeight -
                            staticWidthY -
                            x.layerHeightFromImpermeableLayer / Math.sin(degreesToRadians(90 - slopeAngle)) +
                            x.thickness / Math.sin(degreesToRadians(90 - slopeAngle)),
                    );
                    context.lineTo(
                        campaignPlanLayerHeightX,
                        height - impermeableLayerHeight - x.layerHeightFromImpermeableLayer / Math.sin(degreesToRadians(90 - slopeAngle)) + x.thickness / Math.sin(degreesToRadians(90 - slopeAngle)),
                    );
                    context.closePath();
                    context.fillStrokeShape(shape);
                },
            }),
    );

    var impermeableLayer = new Konva.Shape({
        fillPatternImage: htmlImgObj.impermeableLayerPattern as HTMLImageElement,
        fillPatternScale: {
            x: 0.5,
            y: 0.5,
        },
        opacity: result.status == EReduxState.fulfilled && !onlySlopeAndLoad ? 0.4 : 1,
        sceneFunc: function (context, shape) {
            context.beginPath();
            context.moveTo(campaignPlanLayerHeightX, height - impermeableLayerHeight);
            context.lineTo(campaignPlanLayerHeightX, height);
            context.lineTo(width, height);
            context.lineTo(width, height - impermeableLayerHeight - staticWidthY);
            context.closePath();
            context.fillStrokeShape(shape);
        },
    });

    var groundwaterWithoutDrain = new Konva.Shape({
        fillPatternImage: htmlImgObj.waterPattern as HTMLImageElement,
        fillPatternScale: {
            x: 0.3,
            y: 0.3,
        },
        fillPatternRotation: -slopeAngle,
        opacity: result.status == EReduxState.fulfilled && !onlySlopeAndLoad ? 0.5 : 1,
        sceneFunc: function (context, shape) {
            context.beginPath();
            context.moveTo(campaignPlanLayerHeightX, staticWidthY + campaignPlanLayerHeightY + initialWaterTableDepth / Math.sin(degreesToRadians(90 - slopeAngle)));
            context.lineTo(width, campaignPlanLayerHeightY + initialWaterTableDepth / Math.sin(degreesToRadians(90 - slopeAngle)));
            context.lineTo(width, height - impermeableLayerHeight - staticWidthY);
            context.lineTo(campaignPlanLayerHeightX, height - impermeableLayerHeight);
            context.closePath();
            context.fillStrokeShape(shape);
        },
    });

    var groundwaterWithDrain = new Konva.Shape({
        fillPatternImage: htmlImgObj.waterPattern as HTMLImageElement,
        fillPatternScale: {
            x: 0.3,
            y: 0.3,
        },
        fillPatternRotation: -slopeAngle,
        opacity: result.status == EReduxState.fulfilled && !onlySlopeAndLoad ? 0.5 : 1,
        sceneFunc: function (context, shape) {
            context.beginPath();
            context.moveTo(campaignPlanLayerHeightX, staticWidthY + campaignPlanLayerHeightY + drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)));
            context.lineTo(width, campaignPlanLayerHeightY + drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)));
            context.lineTo(width, height - impermeableLayerHeight - staticWidthY);
            context.lineTo(campaignPlanLayerHeightX, height - impermeableLayerHeight);
            context.closePath();
            context.fillStrokeShape(shape);
        },
    });

    var slopeLine = (u: number) =>
        new Konva.Line({
            points: [
                campaignPlanLayerHeightX + getXProjection(u, slopeAngle) - getXProjection(drainHeight, 90 - slopeAngle),
                staticWidthY +
                    campaignPlanLayerHeightY +
                    drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)) -
                    getYProjection(u, slopeAngle) -
                    getYProjection(drainHeight, 90 - slopeAngle),
                campaignPlanLayerHeightX + getXProjection(u, slopeAngle),
                staticWidthY + campaignPlanLayerHeightY + drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)) - getYProjection(u, slopeAngle),
            ],
            stroke: project.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].product?.color,
            strokeWidth: 0.7,
        });

    var drain = [
        new Konva.Shape({
            fillPatternImage: htmlImgObj.drainPattern as HTMLImageElement,
            fillPatternScale: {
                x: 0.13,
                y: 0.13,
            },
            fillPatternRotation: -slopeAngle,
            sceneFunc: function (context, shape) {
                context.beginPath();
                context.moveTo(
                    campaignPlanLayerHeightX,
                    staticWidthY + campaignPlanLayerHeightY + drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)) - drainHeight / Math.sin(degreesToRadians(90 - slopeAngle)),
                );
                context.lineTo(width, campaignPlanLayerHeightY + drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)) - drainHeight / Math.sin(degreesToRadians(90 - slopeAngle)));
                context.lineTo(width, campaignPlanLayerHeightY + drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)));
                context.lineTo(campaignPlanLayerHeightX, staticWidthY + campaignPlanLayerHeightY + drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)));
                context.closePath();
                context.fillStrokeShape(shape);
            },
        }),
        new Konva.Line({
            points: [
                campaignPlanLayerHeightX,
                staticWidthY + campaignPlanLayerHeightY + drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)) - drainHeight / Math.sin(degreesToRadians(90 - slopeAngle)),
                width,
                campaignPlanLayerHeightY + drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)) - drainHeight / Math.sin(degreesToRadians(90 - slopeAngle)),
            ],
            stroke: project.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].product?.color,
            strokeWidth: 0.7,
        }),
        slopeLine(drainUnitLength / 2),
        slopeLine((3 * drainUnitLength) / 2),
        slopeLine((5 * drainUnitLength) / 2),
    ];

    var groundwaterInsideDrain = new Konva.Shape({
        fill: color.waterInsideDrain,
        opacity: result.status == EReduxState.fulfilled && !onlySlopeAndLoad ? 0.7 : 1,
        sceneFunc: function (context, shape) {
            context.beginPath();
            context.moveTo(
                campaignPlanLayerHeightX,
                staticWidthY +
                    campaignPlanLayerHeightY +
                    drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)) -
                    (drainHeight * fillingPercentage!) / 100 / Math.sin(degreesToRadians(90 - slopeAngle)),
            );
            context.lineTo(
                width,
                campaignPlanLayerHeightY + drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)) - (drainHeight * fillingPercentage!) / 100 / Math.sin(degreesToRadians(90 - slopeAngle)),
            );
            context.lineTo(width, campaignPlanLayerHeightY + drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)));
            context.lineTo(campaignPlanLayerHeightX, staticWidthY + campaignPlanLayerHeightY + drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)));
            context.closePath();
            context.fillStrokeShape(shape);
        },
    });

    let imageOffsetX = 0;
    let imageOffsetY = 15;
    let imagePointX = campaignPlanLayerHeightX + getXProjection(staticWidth / 2 - (impermeableLayerDepth * 1) / Math.tan(degreesToRadians(90 - slopeAngle)), slopeAngle) - imageOffsetX;
    let imagePointY = staticWidthY + campaignPlanLayerHeightY - getYProjection(staticWidth / 2 - (impermeableLayerDepth * 1) / Math.tan(degreesToRadians(90 - slopeAngle)), slopeAngle) - imageOffsetY;

    var backgroundAxisX = new Konva.Rect({
        x: imagePointX + 15,
        y: imagePointY - 5,
        width: staticWidth / 2 + imageOffsetX - 15,
        height: 15,
        fill: withExport ? '#E2F2F4' : 'white',
        stroke: 'transparent',
        rotation: -slopeAngle,
    });

    var axisImage = new Konva.Image({
        x: imagePointX,
        y: imagePointY,
        width: staticWidth / 2 + imageOffsetX,
        height: impermeableLayerDepth + imageOffsetY + 1,
        rotation: -slopeAngle,
        image: htmlImgObj.resultCalculation && htmlImgObj.resultCalculation!.axis ? (htmlImgObj.resultCalculation!.axis as HTMLImageElement) : undefined,
    });

    var preFsStaticImage = new Konva.Image({
        x: imagePointX,
        y: imagePointY,
        width: staticWidth / 2 + imageOffsetX,
        height: impermeableLayerDepth + imageOffsetY + 1,
        rotation: -slopeAngle,
        image: htmlImgObj.resultCalculation && htmlImgObj.resultCalculation!.preFsStatic ? (htmlImgObj.resultCalculation!.preFsStatic as HTMLImageElement) : undefined,
    });

    var postFsStaticImage = new Konva.Image({
        x: imagePointX,
        y: imagePointY,
        width: staticWidth / 2 + imageOffsetX,
        height: impermeableLayerDepth + imageOffsetY + 1,
        rotation: -slopeAngle,
        image: htmlImgObj.resultCalculation && htmlImgObj.resultCalculation!.postFsStatic ? (htmlImgObj.resultCalculation!.postFsStatic as HTMLImageElement) : undefined,
    });

    var preFsSeismicImage = new Konva.Image({
        x: imagePointX,
        y: imagePointY,
        width: staticWidth / 2 + imageOffsetX,
        height: impermeableLayerDepth + imageOffsetY + 1,
        rotation: -slopeAngle,
        image: htmlImgObj.resultCalculation && htmlImgObj.resultCalculation!.preFsSeismic ? (htmlImgObj.resultCalculation!.preFsSeismic as HTMLImageElement) : undefined,
    });

    var postFsSeismicImage = new Konva.Image({
        x: imagePointX,
        y: imagePointY,
        width: staticWidth / 2 + imageOffsetX,
        height: impermeableLayerDepth + imageOffsetY + 1,
        rotation: -slopeAngle,
        image: htmlImgObj.resultCalculation && htmlImgObj.resultCalculation!.postFsSeismic ? (htmlImgObj.resultCalculation!.postFsSeismic as HTMLImageElement) : undefined,
    });

    var drainageChimney = project.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].hasDrainageChimney
        ? new Konva.Rect({
              x:
                  campaignPlanLayerHeightX +
                  getXProjection((3 * drainUnitLength) / 2, slopeAngle) -
                  getXProjection(drainHeight, 90 - slopeAngle) -
                  getXProjection(drainageChimneyLength, slopeAngle) -
                  getXProjection(drainageChimneyHeight, 90 - slopeAngle),
              y:
                  staticWidthY +
                  campaignPlanLayerHeightY +
                  drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)) -
                  getYProjection((3 * drainUnitLength) / 2, slopeAngle) -
                  getYProjection(drainHeight, 90 - slopeAngle) +
                  getYProjection(drainageChimneyLength, slopeAngle) -
                  getYProjection(drainageChimneyHeight, 90 - slopeAngle),
              width: drainageChimneyLength,
              height: drainageChimneyHeight,
              fillPatternImage: htmlImgObj.drainPattern as HTMLImageElement,
              fillPatternScale: {
                  x: 0.13,
                  y: 0.13,
              },
              stroke: project.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].product?.color,
              strokeWidth: 0.7,
              rotation: -slopeAngle,
          })
        : new Konva.Rect({});

    var referenceCampaignPlanLine = new Konva.Line({
        points: [campaignPlanLayerHeightX, staticWidthY + campaignPlanLayerHeightY, width, campaignPlanLayerHeightY],
        stroke: 'black',
        strokeWidth: 0.3,
        lineJoin: 'round',
    });

    var referenceInitialWaterTableDepth = new Konva.Line({
        points: [
            campaignPlanLayerHeightX,
            staticWidthY + campaignPlanLayerHeightY + initialWaterTableDepth / Math.sin(degreesToRadians(90 - slopeAngle)),
            width,
            campaignPlanLayerHeightY + initialWaterTableDepth / Math.sin(degreesToRadians(90 - slopeAngle)),
        ],
        stroke: color.initialWaterTableDepth,
        strokeWidth: 1,
        lineJoin: 'round',
        dash: [3, 3],
    });

    var referenceWaterLine = new Konva.Line({
        points: [
            campaignPlanLayerHeightX,
            staticWidthY + campaignPlanLayerHeightY + drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)),
            width,
            campaignPlanLayerHeightY + drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)),
        ],
        stroke: color.water,
        strokeWidth: 2,
        opacity: result.status == EReduxState.fulfilled && !onlySlopeAndLoad ? 0.5 : 1,
        lineJoin: 'round',
    });

    var referenceDrainBaseLine = new Konva.Line({
        points: [
            campaignPlanLayerHeightX,
            staticWidthY + campaignPlanLayerHeightY + drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)),
            width,
            campaignPlanLayerHeightY + drainPositionDepth / Math.sin(degreesToRadians(90 - slopeAngle)),
        ],
        stroke: 'black',
        offsetY: -0.5,
        strokeWidth: 1,
        dash: [3, 3],
    });

    var campaignPlanText = new Konva.Text({
        x: campaignPlanLayerHeightX,
        y: staticWidthY - campaignPlanLayerHeightY - 7,
        text: i18next.t('campaignPlan'),
        fontSize: 5,
        fontFamily: 'Calibri',
        fill: 'black',
        fontStyle: 'bold',
    });

    var impermeableLayerText = new Konva.Text({
        x: width - 50,
        y: height - impermeableLayerHeight - 10,
        text: i18next.t('waterproofLayer'),
        fontSize: 5,
        fontFamily: 'Calibri',
        fill: 'black',
        fontStyle: 'bold',
    });

    var backgroundImpermeableLayerText = new Konva.Rect({
        x: impermeableLayerText.x(),
        y: impermeableLayerText.y(),
        width: impermeableLayerText.width() + 3.5,
        height: impermeableLayerText.height(),
        fill: 'white',
    });

    var angleIndicator = setAngleIndicator(campaignPlanLayerHeightX, staticWidthY + campaignPlanLayerHeightY, slopeAngle!);

    if (distributedOverload! > 0) {
        if (result.status == EReduxState.fulfilled && !onlySlopeAndLoad) {
            group.add(...arrowDistributedOverload, backgroundAxisX);
        } else group.add(...arrowDistributedOverload);
    }

    if (onlySlopeAndLoad) {
        group.add(
            rect,
            longitudinalSection,
            campaignPlanLayer,
            impermeableLayer,
            ...soilLayers,
            groundwaterWithoutDrain,
            referenceCampaignPlanLine,
            referenceInitialWaterTableDepth,
            campaignPlanText,
            backgroundImpermeableLayerText,
            impermeableLayerText,
            ...angleIndicator,
        );
    } else {
        group.add(
            rect,
            longitudinalSection,
            campaignPlanLayer,
            impermeableLayer,
            ...soilLayers,
            result.status == EReduxState.fulfilled && !onlySlopeAndLoad ? groundwaterWithDrain : new Konva.Rect(),
            result.status == EReduxState.fulfilled && !onlySlopeAndLoad ? referenceWaterLine : new Konva.Line(),
            ...drain,
            drainageChimney,
            result.status == EReduxState.fulfilled && !onlySlopeAndLoad ? groundwaterInsideDrain : new Konva.Rect(),
            referenceCampaignPlanLine,
            referenceInitialWaterTableDepth,
            referenceDrainBaseLine,
            campaignPlanText,
            backgroundImpermeableLayerText,
            impermeableLayerText,
            preFsStaticImage,
            postFsStaticImage,
            preFsSeismicImage,
            postFsSeismicImage,
            axisImage,
            ...angleIndicator,
        );
    }

    layer.add(group);

    return { width, height, layer };
};
